import type { EmailBlockAttributes, EmailBlockSchema, EmailBlockType } from './catalog';

/**
 * Tree helpers for the visual email builder. The stack is mostly flat, but one block type — the
 * `header` row — holds children (the logo image + brand-name text laid out side by side), so these
 * helpers recurse one (or more) levels. Modeled on the cart-widget builder's tree helpers
 * (apps/valet/src/builder/useBuilderTree.ts); kept separate (distinct dir + store refs) so the two
 * features never collide on merge.
 */

/**
 * A persisted email block plus a transient, editor-only id (`__builderId`) used for selection and
 * drag-and-drop. The id is stripped before the list is persisted (see {@link stripIds}).
 */
export interface EmailBlock {
  __builderId: string;
  type: EmailBlockType;
  attributes: EmailBlockAttributes;
  children?: EmailBlock[];
}

/** A block as persisted (no editor id). */
export interface PersistedEmailBlock {
  type: EmailBlockType;
  attributes: EmailBlockAttributes;
  children?: PersistedEmailBlock[];
}

export interface BlockLocation {
  /** The array (root or a parent's children) that directly contains the block. */
  parentArray: EmailBlock[];
  index: number;
  block: EmailBlock;
}

let idCounter = 0;

/** Generates a process-unique editor id. Not persisted, so cross-session collisions are irrelevant. */
export function createBlockId(): string {
  idCounter += 1;

  return `eb_${idCounter}_${Math.random().toString(36).slice(2, 9)}`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function hydrateBlock(block: PersistedEmailBlock): EmailBlock {
  return {
    __builderId: createBlockId(),
    attributes: { ...(block.attributes || {}) },
    type: block.type,
    ...(block.children ? { children: block.children.map(hydrateBlock) } : {}),
  };
}

/** Assigns a fresh `__builderId` to every block in a persisted list (recursively). */
export function hydrateIds(blocks: PersistedEmailBlock[]): EmailBlock[] {
  return blocks.map(hydrateBlock);
}

function stripBlock(block: EmailBlock): PersistedEmailBlock {
  return {
    attributes: { ...block.attributes },
    type: block.type,
    ...(block.children ? { children: block.children.map(stripBlock) } : {}),
  };
}

/** Clones a builder list back into a clean persisted list, dropping editor ids (recursively). */
export function stripIds(blocks: EmailBlock[]): PersistedEmailBlock[] {
  return blocks.map(stripBlock);
}

/** True when a value looks like a persisted email block list. */
export function isBlockList(value: unknown): value is PersistedEmailBlock[] {
  return (
    Array.isArray(value) &&
    value.every(b => !!b && typeof b === 'object' && typeof (b as PersistedEmailBlock).type === 'string')
  );
}

/** Parses a stored value into a builder block list. Returns [] for anything that isn't a block list. */
export function parseBlocks(value: unknown): EmailBlock[] {
  if (!isBlockList(value)) return [];

  return hydrateIds(value);
}

/** Finds a block and its location (parent array + index), recursing into children. */
export function locateBlock(blocks: EmailBlock[], id: string): BlockLocation | null {
  for (let index = 0; index < blocks.length; index += 1) {
    const block = blocks[index];
    if (block.__builderId === id) return { block, index, parentArray: blocks };

    if (block.children) {
      const found = locateBlock(block.children, id);
      if (found) return found;
    }
  }

  return null;
}

export function findBlock(blocks: EmailBlock[], id: string): EmailBlock | null {
  return locateBlock(blocks, id)?.block ?? null;
}

/** In-place removal (recursive). Returns the removed block or null. */
export function removeBlock(blocks: EmailBlock[], id: string): EmailBlock | null {
  const location = locateBlock(blocks, id);
  if (!location) return null;

  return location.parentArray.splice(location.index, 1)[0] ?? null;
}

/** In-place insert at `index` in the root list (clamped). */
export function insertBlock(blocks: EmailBlock[], index: number, block: EmailBlock): boolean {
  blocks.splice(clamp(index, 0, blocks.length), 0, block);

  return true;
}

/** In-place move of a root-level block to a new root index (interpreted after removal). */
export function moveBlock(blocks: EmailBlock[], id: string, index: number): boolean {
  const rootIndex = blocks.findIndex(b => b.__builderId === id);
  if (rootIndex === -1) return false;

  const [removed] = blocks.splice(rootIndex, 1);
  blocks.splice(clamp(index, 0, blocks.length), 0, removed);

  return true;
}

/** In-place attribute update (recursive). Replaces the attributes object so shallow watchers react. */
export function setBlockAttr(
  blocks: EmailBlock[],
  id: string,
  attr: string,
  value: string | number | boolean,
): boolean {
  const block = findBlock(blocks, id);
  if (!block) return false;

  block.attributes = { ...block.attributes, [attr]: value };

  return true;
}

/** Duplicates a block (fresh ids, recursively) and inserts the copy directly after the original. */
export function duplicateBlock(blocks: EmailBlock[], id: string): EmailBlock | null {
  const location = locateBlock(blocks, id);
  if (!location) return null;

  const clone = hydrateIds(stripIds([location.block]))[0];
  location.parentArray.splice(location.index + 1, 0, clone);

  return clone;
}

/** True if a block of `type` exists anywhere in the tree (used to enforce singleton block types). */
export function blockTypeExists(blocks: EmailBlock[], type: EmailBlockType): boolean {
  return blocks.some(b => b.type === type || (b.children ? blockTypeExists(b.children, type) : false));
}

/** Builds a fresh block from a catalog entry, seeding its default attributes (and an empty child list for containers). */
export function buildBlockFromSchema(schema: EmailBlockSchema): EmailBlock {
  return {
    __builderId: createBlockId(),
    attributes: { ...schema.defaultAttributes },
    type: schema.type,
    ...(schema.canHaveChildren ? { children: [] } : {}),
  };
}

/** Short human-readable detail for a block row (e.g. the text content). */
export function getBlockSummary(block: EmailBlock): string {
  const attrs = block.attributes || {};
  const text = attrs.text ?? attrs.footerContent;
  if (typeof text === 'string' && text.trim()) {
    const stripped = text
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    return stripped.length > 40 ? `${stripped.slice(0, 40)}…` : stripped;
  }

  if (typeof attrs.src === 'string' && attrs.src) return attrs.src.split('/').pop() || attrs.src;
  if (typeof attrs.logoUrl === 'string' && attrs.logoUrl) return attrs.logoUrl.split('/').pop() || attrs.logoUrl;

  return '';
}
